import React, { useState, useEffect } from "react";
import { FaMoneyBillWave, FaCreditCard, FaHistory, FaCheckCircle, FaCalendarAlt, FaChartLine } from "react-icons/fa";
import Pagination from "../../components/Pagination.tsx";
import axios from "axios";
import Loader from "../../components/Loader.tsx";
import { themes } from "../../theme.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";

interface Payment {
  id: number;
  date: string;
  amount: string;
  status: string;
}

const Payments: React.FC = () => {
  const { themeName } = useTheme();
  const t = (themes as any)[themeName].colors as Record<string, string>;

  // Mock data - replace with actual data from your API
  const paymentStats = {
    totalSpent: "Rs )",
    lastPayment: "Rs 0",
    nextPayment: "Rs 0",
    paymentStatus: "Active"
  };

  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;


  const [paymentHistory, setPaymentHistory] = useState<Payment[]>([])
  const [loading, setLoading] = useState(false)

  const getPaymentHistory = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${process.env.REACT_APP_DB_URI}/users/payment/my-payments/`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      })
      // console.log(response)
      setPaymentHistory(response?.data || [])
      setLoading(false)
    } catch (err) {
      console.log(err)
      setLoading(false)
    }
  }

  useEffect(() => {
    getPaymentHistory();
  }, []);

  return (

    <div className="h-screen flex flex-col overflow-hidden" style={{ background: t.background }}>
      {/* Header */}
      <div className="p-4 flex-shrink-0" style={{
        background: themeName === 'light' ? `linear-gradient(to right, ${t.primary}, ${t.success})` : t.primary,
      }}>
        <div className="">
          <h2 className="text-xl font-bold mb-4" style={{ color: t.text }}>Payment Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="backdrop-blur-sm rounded-lg p-3" style={{ background: themeName === 'light' ? 'rgba(255,255,255,0.1)' : t.surface + '22' }}>
              <div className="flex items-center gap-2 mb-1">
                <FaMoneyBillWave size={18} style={{ color: t.text }} />
                <h3 className="text-base font-semibold" style={{ color: t.text }}>Total Spent</h3>
              </div>
              <p className="text-2xl font-bold" style={{ color: t.text }}>Rs {paymentStats.totalSpent}</p>
            </div>
            <div className="backdrop-blur-sm rounded-lg p-3" style={{ background: themeName === 'light' ? 'rgba(255,255,255,0.1)' : t.surface + '22' }}>
              <div className="flex items-center gap-2 mb-1">
                <FaCreditCard size={18} style={{ color: t.text }} />
                <h3 className="text-base font-semibold" style={{ color: t.text }}>Last Payment</h3>
              </div>
              <p className="text-2xl font-bold" style={{ color: t.text }}>Rs {paymentStats.lastPayment}</p>
            </div>
            <div className="backdrop-blur-sm rounded-lg p-3" style={{ background: themeName === 'light' ? 'rgba(255,255,255,0.1)' : t.surface + '22' }}>
              <div className="flex items-center gap-2 mb-1">
                <FaCalendarAlt size={18} style={{ color: t.text }} />
                <h3 className="text-base font-semibold" style={{ color: t.text }}>Next Payment</h3>
              </div>
              <p className="text-2xl font-bold" style={{ color: t.text }}>Rs {paymentStats.nextPayment}</p>
            </div>
            <div className="backdrop-blur-sm rounded-lg p-3" style={{ background: themeName === 'light' ? 'rgba(255,255,255,0.1)' : t.surface + '22' }}>
              <div className="flex items-center gap-2 mb-1">
                <FaChartLine size={18} style={{ color: t.text }} />
                <h3 className="text-base font-semibold" style={{ color: t.text }}>Payment Status</h3>
              </div>
              <p className="text-2xl font-bold" style={{ color: t.text }}>{paymentStats.paymentStatus}</p>
            </div>
          </div>
        </div>
      </div>

      {loading ?
        <Loader />
        :
        <div className="flex-1 overflow-y-auto" style={{ background: t.background }}>
          <div className="">
            {/* Payment History Table */}
            <div className="rounded-xl shadow-lg overflow-hidden p-6 border mb-6" style={{ background: t.surface, borderColor: t.border }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: t.text }}>📋 Payment History</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead style={{ background: t.primary }}>
                    <tr>
                      <th className="p-3 text-left" style={{ color: t.inverseText }}>Date</th>
                      <th className="p-3 text-left" style={{ color: t.inverseText }}>Amount</th>
                      <th className="p-3 text-left" style={{ color: t.inverseText }}>Status</th>
                      <th className="p-3 text-left" style={{ color: t.inverseText }}>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory?.length > 0 &&
                      paymentHistory?.map((payment) => (
                        <tr key={payment.id} className="border-b" style={{ borderColor: t.border }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeName === 'light' ? t.card : t.surface + '88'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = themeName === 'light' ? 'transparent' : t.surface}>
                          <td className="p-3" style={{ color: t.text }}>{new Date(payment?.date).toLocaleDateString()}</td>
                          <td className="p-3" style={{ color: t.text }}>Rs {payment?.amount}</td>
                          <td className="p-3">
                            <span className="px-2 py-1 rounded-full text-xs"
                              style={{
                                background: payment?.status === "Completed" ? t.success + '22' : t.warning + '22',
                                color: payment?.status === "Completed" ? t.success : t.warning
                              }}>
                              {payment?.status}
                            </span>
                          </td>
                          {/* <td className="p-3">
                        <button className="text-teal-600 hover:text-teal-800">
                          Download
                        </button>
                      </td> */}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Action Buttons */}
            {/* <div className="flex justify-end gap-4 mb-6">
            <button className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700">
              Make Payment
            </button>
            <button className="bg-white text-teal-600 border border-teal-600 px-4 py-2 rounded-lg hover:bg-teal-50">
              Download All Invoices
            </button>
          </div> */}
          </div>
        </div>
      }
    </div>
  );
};

export default Payments;